import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import LiveTablesPanel from '../../components/Admin/LiveTablesPanel';
import SalesHistory from '../../components/Admin/SalesHistory';
import SalesChartPanel from '../../components/Admin/SalesChartPanel';
import ManualOrderPanel from '../../components/Admin/ManualOrderPanel';
import SystemSettingsPanel from '../../components/Admin/SystemSettingsPanel';
import TicketSettingsPanel from '../../components/Admin/TicketSettingsPanel';
import AdvancedAnalytics from '../../components/Admin/AdvancedAnalytics';
import KitchenPanel from '../../components/Admin/KitchenPanel';
import BarPanel from '../../components/Admin/BarPanel';
import TablesManagement from '../../components/Admin/TablesManagement';
import ProfitDashboard from '../../components/Admin/ProfitDashboard';
import ExpensesPanel from '../../components/Admin/ExpensesPanel';
import CategoriesPanel from '../../components/Admin/CategoriesPanel';
import VendorsPanel from '../../components/Admin/ExpenseDetail/VendorsPanel';
import BanksPanel from '../../components/Admin/Banks/BanksPanel';
import ExpenseCategoryDetail from '../../components/Admin/ExpenseDetail/ExpenseCategoryDetail';
import ProductsPanel from '../../components/Admin/ProductsPanel';
import TimeClockAdminPanel from '../../components/Admin/TimeClockAdminPanel';
import TimeClockPublicView from '../../components/Admin/TimeClockPublicView';
import MomentoConfigPanel from '../../components/Admin/MomentoConfigPanel';
import TurboMarketingPanel from '../../components/Admin/TurboMarketingPanel';
import ImageLibraryPanel from '../../components/Admin/ImageLibraryPanel';
import GuidedFlowAdmin from '../../components/Admin/GuidedFlowAdmin';
import ArqueoPanel from '../../components/Admin/ArqueoPanel';
import IntelligenceDashboard from './IntelligenceDashboard';
import ReviewSettingsPage from './ReviewSettingsPage';
import { 
  ChefHat, Wine, Table2, Receipt, PlusCircle, LogOut, 
  Settings, Lock, Clock, Euro, Users, TrendingUp,
  FolderOpen, Package, QrCode, BarChart3, DollarSign, Star, Menu, X,
  Wallet, FileText, UserCheck, Sparkles, Zap, Bell, BellRing, Maximize, Minimize,
  ChevronLeft, Image as ImageIcon, Calculator
} from 'lucide-react';

import { useShiftAlarms } from '../../hooks/useShiftAlarms';
import { useFullscreen } from '../../hooks/useFullscreen';
import useSmartPolling from '../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== MAIN OPERATIONS DASHBOARD ====================

const OperationsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [mesasRes, ventasRes, liveRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/mesas/live`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/ventas/stats?periodo=hoy`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/ventas/live-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const mesas = mesasRes.ok ? await mesasRes.json() : [];
      const ventas = ventasRes.ok ? await ventasRes.json() : {};
      const live = liveRes.ok ? await liveRes.json() : {};
      
      const ocupadas = mesas.filter(m => m.status === 'ocupada');
      const consumoActivo = ocupadas.reduce((sum, m) => sum + (m.session?.total || 0), 0);
      
      setStats({
        totalMesas: mesas.length,
        ocupadas: ocupadas.length,
        consumoActivo,
        ventasHoy: ventas.total_ventas || 0,
        ingresosHoy: ventas.total_ingresos || 0,
        ticketMedio: ventas.ticket_medio || 0
      });
      setLiveStats(live);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useSmartPolling(fetchStats, 30000);

  const quickActions = [
    { path: '/admin/nuevo-pedido', label: 'Nuevo Pedido', icon: PlusCircle, color: '#4CAF50', desc: 'Crear pedido manual' },
    { path: '/admin/cocina', label: 'Cocina', icon: ChefHat, color: '#FF9800', desc: 'Pedidos para preparar' },
    { path: '/admin/barra', label: 'Barra', icon: Wine, color: '#9C27B0', desc: 'Bebidas y cafés' },
    { path: '/admin/mesas-live', label: 'Mesas', icon: Table2, color: '#2196F3', desc: 'Estado en vivo' },
    { path: '/admin/ventas', label: 'Ventas', icon: Receipt, color: '#607D8B', desc: 'Historial del día' },
    { path: '/admin/arqueo', label: 'Arqueo', icon: Calculator, color: '#D4AF37', desc: 'Cierre de caja con IA' },
    { path: '/admin/registro-horario', label: 'Control Horario', icon: Clock, color: '#7C3AED', desc: 'Registros y fichajes' },
  ];

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Buenos días' : now.getHours() < 20 ? 'Buenas tardes' : 'Buenas noches';

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="operations-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-light text-gray-800 mb-1">{greeting}</h1>
        <p className="text-gray-500">
          {now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Users className="text-blue-500" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-800">{stats.ocupadas}</p>
                <p className="text-xs text-gray-500">Mesas ocupadas</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <Euro className="text-green-500" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-800">{stats.consumoActivo.toFixed(0)}€</p>
                <p className="text-xs text-gray-500">En mesas</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Receipt className="text-amber-500" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-800">{stats.ventasHoy}</p>
                <p className="text-xs text-gray-500">Ventas hoy</p>
              </div>
            </div>
          </div>
          
          <div className="col-span-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <TrendingUp className="text-white" size={20} />
              </div>
              <div>
                <p className="text-3xl font-semibold text-white">{stats.ingresosHoy.toFixed(2)}€</p>
                <p className="text-sm text-white/80">Ingresos del día</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live Insights: Top 3 Products + Top Category */}
      {liveStats && (liveStats.top_products?.length > 0 || liveStats.top_category) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {/* Top 3 Products */}
          {liveStats.top_products?.length > 0 && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="top-products-card">
              <div className="flex items-center gap-2 mb-3">
                <Star className="text-amber-500" size={18} />
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Top productos hoy</h3>
              </div>
              <div className="space-y-2.5">
                {liveStats.top_products.map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold text-white ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-gray-400' : 'bg-amber-700'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                      <p className="text-xs text-gray-400">{p.quantity} uds</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{p.revenue.toFixed(2)}€</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Category */}
          {liveStats.top_category && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="top-category-card">
              <div className="flex items-center gap-2 mb-3">
                <FolderOpen className="text-blue-500" size={18} />
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Categoría líder hoy</h3>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="text-blue-500" size={28} />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-800">{liveStats.top_category.name}</p>
                  <p className="text-2xl font-bold text-blue-600">{liveStats.top_category.revenue.toFixed(2)}€</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.path}
            to={action.path}
            className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300"
            data-testid={`action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
              style={{ backgroundColor: `${action.color}15` }}
            >
              <action.icon size={28} style={{ color: action.color }} />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-1">{action.label}</h3>
            <p className="text-sm text-gray-500">{action.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ==================== ADMIN SECTION (Protected) ====================

const AdminSection = ({ onBack }) => {
  const adminItems = [
    { path: '/admin/categories', label: 'Categorías', icon: FolderOpen, desc: 'Gestionar categorías del menú' },
    { path: '/admin/products', label: 'Productos', icon: Package, desc: 'Añadir y editar productos' },
    { path: '/admin/imagen-biblioteca', label: 'Biblioteca de Imágenes', icon: ImageIcon, desc: 'Subir y gestionar fotos de platos' },
    { path: '/admin/flujo-guiado', label: 'Flujo Guiado', icon: Sparkles, desc: 'Configurar recomendaciones y embudo' },
    { path: '/admin/mesas-config', label: 'Configurar Mesas', icon: QrCode, desc: 'Gestionar mesas y QRs' },
    { path: '/admin/momento', label: 'Modo Momento', icon: Sparkles, desc: 'Configurar popup y banners' },
    { path: '/admin/turbo-marketing', label: 'Turbo Marketing', icon: Zap, desc: 'Banners de upsell' },
    { path: '/admin/ticket-config', label: 'Ticket', icon: FileText, desc: 'Personalizar ticket cliente' },
    { path: '/admin/control-horario', label: 'Control Horario', icon: UserCheck, desc: 'Fichajes y jornadas' },
    { path: '/admin/graficos', label: 'Gráficos', icon: TrendingUp, desc: 'Análisis por horas' },
    { path: '/admin/gastos', label: 'Gastos', icon: Wallet, desc: 'Control de gastos' },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3, desc: 'Dashboard avanzado' },
    { path: '/admin/intelligence', label: 'Inteligencia', icon: BarChart3, desc: 'Analytics avanzado' },
    { path: '/admin/profit', label: 'Profit', icon: DollarSign, desc: 'Análisis de beneficios' },
    { path: '/admin/reviews', label: 'Reseñas', icon: Star, desc: 'Gestionar reseñas' },
    { path: '/admin/configuracion', label: 'Sistema', icon: Settings, desc: 'Ajustes del sistema' },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" onClick={onBack} className="rounded-xl">
          ← Volver
        </Button>
        <div>
          <h1 className="text-2xl font-medium text-gray-800">Administración</h1>
          <p className="text-gray-500 text-sm">Configuración avanzada del sistema</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {adminItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="flex items-center gap-4 bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <item.icon size={24} className="text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-800">{item.label}</h3>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN DASHBOARD COMPONENT ====================

const AdminDashboardNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { alarms, hasAlarms, getUnnotified } = useShiftAlarms();
  const { isFullscreen, toggleFullscreen } = useFullscreen();

  const isSubSection = location.pathname !== '/admin' && location.pathname !== '/admin/';

  // Global alarm toast notifications
  useEffect(() => {
    const newAlarms = getUnnotified();
    newAlarms.forEach(alarm => {
      const isEntry = alarm.type === 'clock_in';
      toast(alarm.message, {
        icon: isEntry ? '🟢' : '🔴',
        duration: 15000,
        style: {
          border: isEntry ? '2px solid #22c55e' : '2px solid #ef4444',
          fontWeight: 600,
        },
      });
      // Play sound
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = isEntry ? 880 : 660;
        osc.type = 'sine';
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.frequency.value = isEntry ? 1100 : 440;
          osc2.type = 'sine';
          gain2.gain.value = 0.15;
          osc2.start();
          osc2.stop(ctx.currentTime + 0.3);
        }, 350);
      } catch (e) {}
    });
  }, [alarms, getUnnotified]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await authApi.me();
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleAdminAccess = () => {
    // Simple password check (in production, this should be a backend call)
    if (adminPassword === 'pont10admin') {
      setIsAdminMode(true);
      setShowAdminDialog(false);
      setAdminPassword('');
      toast.success('Acceso de administrador activado');
    } else {
      toast.error('Contraseña incorrecta');
    }
  };

  const dailyNavItems = [
    { path: '/admin', label: 'Inicio', icon: Table2 },
    { path: '/admin/nuevo-pedido', label: 'Nuevo Pedido', icon: PlusCircle },
    { path: '/admin/cocina', label: 'Cocina', icon: ChefHat },
    { path: '/admin/barra', label: 'Barra', icon: Wine },
    { path: '/admin/mesas-live', label: 'Mesas', icon: Users },
    { path: '/admin/ventas', label: 'Ventas', icon: Receipt },
    { path: '/admin/arqueo', label: 'Arqueo', icon: Calculator },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      
      {/* Top Bar */}
      <header className="admin-topbar bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="px-3 sm:px-4">
          <div className="flex items-center h-14 sm:h-16 gap-2">
            {/* Actions — moved to LEFT */}
            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
              {/* Fullscreen */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className={`p-2 min-w-[44px] min-h-[44px] ${isFullscreen ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'}`}
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                data-testid="global-fullscreen-btn"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </Button>
              {/* Alarm Bell */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/control-horario')}
                  className={`p-2 min-w-[44px] min-h-[44px] ${hasAlarms ? 'text-amber-500 animate-pulse' : 'text-gray-400'}`}
                  data-testid="alarm-bell-btn"
                >
                  {hasAlarms ? <BellRing size={20} /> : <Bell size={20} />}
                  {hasAlarms && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {alarms.length}
                    </span>
                  )}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdminDialog(true)}
                className="p-2 min-w-[44px] min-h-[44px] text-gray-500 hover:text-gray-700"
                data-testid="admin-access-btn"
              >
                <Lock size={18} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="p-2 px-3 min-w-[44px] min-h-[44px] text-red-400 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
              </Button>
            </div>

            {/* Back button — visible on sub-sections */}
            {isSubSection && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors text-sm font-medium"
                data-testid="back-to-home-btn"
              >
                <ChevronLeft size={18} />
                <span className="hidden sm:inline">Inicio</span>
              </button>
            )}

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto no-scrollbar mx-2">
              {dailyNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-1.5 px-2.5 lg:px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  <item.icon size={17} />
                  <span className="hidden xl:inline">{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Logo — moved to RIGHT */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              <Badge variant="outline" className="hidden xl:flex text-xs">
                Staff
              </Badge>
              <span className="font-serif text-lg sm:text-xl tracking-tight whitespace-nowrap">
                PONT <span className="text-green-600">10</span>
              </span>
              <button
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <nav className="p-2 space-y-1">
              {dailyNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-100"
                >
                  <item.icon size={20} />
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="admin-main pb-20">
        <Routes>
          <Route index element={<OperationsDashboard />} />
          <Route path="nuevo-pedido" element={<ManualOrderPanel />} />
          <Route path="cocina" element={<KitchenPanel />} />
          <Route path="barra" element={<BarPanel />} />
          <Route path="mesas-live" element={<LiveTablesPanel />} />
          <Route path="ventas" element={<SalesHistory />} />
          <Route path="graficos" element={<SalesChartPanel />} />
          <Route path="gastos" element={<ExpensesPanel />} />
          <Route path="gastos/categoria/:slug" element={<ExpenseCategoryDetail />} />
          <Route path="gastos/proveedores" element={<VendorsPanel />} />
          <Route path="gastos/pagos" element={<BanksPanel />} />
          <Route path="gastos/bancos" element={<Navigate to="/admin/gastos/pagos" replace />} />
          <Route path="gastos/proveedor/:name" element={<ExpenseCategoryDetail key="vendor" />} />
          <Route path="analytics" element={<AdvancedAnalytics />} />
          <Route path="registro-horario" element={<TimeClockPublicView />} />
          
          {/* Admin Routes */}
          <Route path="admin-menu" element={<AdminSection onBack={() => navigate('/admin')} />} />
          <Route path="categories" element={<CategoriesPanel />} />
          <Route path="products" element={<ProductsPanel />} />
          <Route path="mesas-config" element={<TablesManagement />} />
          <Route path="ticket-config" element={<TicketSettingsPanel />} />
          <Route path="control-horario" element={<TimeClockAdminPanel />} />
          <Route path="configuracion" element={<SystemSettingsPanel />} />
          <Route path="profit" element={<ProfitDashboard />} />
          <Route path="intelligence" element={<IntelligenceDashboard />} />
          <Route path="reviews" element={<ReviewSettingsPage />} />
          <Route path="momento" element={<MomentoConfigPanel />} />
          <Route path="turbo-marketing" element={<TurboMarketingPanel />} />
          <Route path="imagen-biblioteca" element={<ImageLibraryPanel />} />
          <Route path="flujo-guiado" element={<GuidedFlowAdmin />} />
          <Route path="arqueo" element={<ArqueoPanel />} />
        </Routes>
      </main>

      {/* Admin Access Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={20} />
              Acceso Administrador
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Introduce la contraseña de administrador para acceder a la configuración avanzada.
            </p>
            <Input
              type="password"
              placeholder="Contraseña de admin"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdminAccess()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdminAccess} className="bg-green-600 hover:bg-green-700">
              Acceder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Mode Floating Button - Centered */}
      {isAdminMode && !isFullscreen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <Link
            to="/admin/admin-menu"
            className="flex items-center gap-2 bg-gray-900 text-white px-5 py-3 rounded-full shadow-xl hover:bg-gray-800 transition-colors"
          >
            <Settings size={18} />
            Administración
          </Link>
        </div>
      )}

      {/* Soft Fullscreen Exit Button - floating when top bar is hidden */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="fixed top-3 right-3 z-[60] bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full shadow-lg backdrop-blur-sm transition-all"
          data-testid="exit-fullscreen-btn"
          title="Salir de pantalla completa"
        >
          <Minimize size={20} />
        </button>
      )}
    </div>
  );
};

export default AdminDashboardNew;
